import { searchReportingFetch } from '../lib/flags/search-reporting';
import { getAuthToken } from './auth';
import { notificationsClient } from '../lib/trpc/notifications-main';

const OPENSEARCH_URL = import.meta.env.VITE_APP_OPENSEARCH_URL;

export interface ExportHeader {
  label: string;
  key: string;
}

export interface StartExportParams {
  entityType:
    | 'contacts'
    | 'subscriptions'
    | 'payouts'
    | 'payouts-drilldown'
    | 'payouts-report'
    | 'transactions'
    | 'forms'
    | 'elements'
    | 'fundraiser-drilldown'
    | 'event-drilldown'
    | 'broadcasts'
    | 'segments';
  filters: Record<string, any>;
  headers: ExportHeader[];
  selectedIds?: string[];
  excludedIds?: string[];
  userEmail?: string;
}

export interface StartExportResponse {
  jobId: string;
}

export interface ExportStatusResponse {
  jobId: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  entityType: string;
  recordCount: number;
  createdAt: number;
  completedAt?: number;
  downloadUrl?: string;
  error?: string;
  processedCount?: number;
  totalCount?: number;
  successCount?: number;
  failedCount?: number;
  /** CON-857: contacts skipped for having no gifts (tax-documents ZIP); absent on old jobs. */
  skippedCount?: number;
  /**
   * The backend DERIVED this terminal state because the job stopped reporting
   * progress, rather than reading it from the stored row (PE-1857).
   *
   * A worker killed before its try/except — an init crash, an OOM, a 15-minute
   * timeout — never writes FAILED, so the row stays non-terminal until its 48h
   * TTL and this panel used to spin on it forever. `status` already arrives as
   * FAILED in that case, and `error` carries the copy to render. This flag only
   * tells you WHERE the verdict came from; nothing needs to branch on it today.
   *
   * The stored row is deliberately NOT rewritten, so a job recovered by DLQ
   * redrive still reports COMPLETED on a later poll.
   */
  stale?: boolean;
}

export interface StartTaxDocumentsParams {
  year: number;
  filters: Record<string, any>;
  selectedIds?: string[];
  excludedIds?: string[];
  userEmail?: string;
  /** CON-829: one paid currency (ISO 4217); absent = every currency. */
  currency?: string;
}

export async function startTaxDocuments(
  params: StartTaxDocumentsParams,
): Promise<StartExportResponse> {
  const response = await notificationsClient.startTaxDocuments(params, await getAuthToken());

  if (!response.ok) {
    const error = (await response.json().catch(() => ({ error: 'Failed to start' }))) as {
      error?: string;
    };
    throw new Error(error.error || 'Failed to start tax documents download');
  }

  return response.json();
}

export async function startEmailTaxDocuments(
  params: StartTaxDocumentsParams,
): Promise<StartExportResponse> {
  const response = await notificationsClient.startEmailTaxDocuments(params, await getAuthToken());

  if (!response.ok) {
    const error = (await response.json().catch(() => ({ error: 'Failed to start' }))) as {
      error?: string;
    };
    throw new Error(error.error || 'Failed to start emailing tax receipts');
  }

  return response.json();
}

export async function startExport(params: StartExportParams): Promise<StartExportResponse> {
  if (params.entityType === 'event-drilldown' && params.filters.contactIDs?.length > 0) {
    throw new Error('This contact filter cannot be exported accurately. Clear the contact filter or select individual attendees instead.');
  }
  if (params.entityType === 'event-drilldown' && Array.isArray(params.filters.attendeeConfirmationIDs)) {
    const { attendeeConfirmationIDs, attendeeNames = [], confirmationIDs = [], ...filters } = params.filters;
    if (attendeeNames.length > 0 && (attendeeConfirmationIDs.length > 0 || attendeeNames.some((name: string) => !name.trim()))) {
      throw new Error('This attendee filter cannot be exported accurately. Select identified attendees only or clear the attendee filter.');
    }
    const selectedConfirmationIDs = attendeeConfirmationIDs.length > 0
      ? attendeeConfirmationIDs.filter((id: string) => confirmationIDs.length === 0 || confirmationIDs.includes(id))
      : confirmationIDs;
    if (attendeeConfirmationIDs.length > 0 && selectedConfirmationIDs.length === 0) {
      throw new Error('The attendee and confirmation filters have no tickets in common.');
    }
    params = { ...params, filters: { ...filters, confirmationIDs: selectedConfirmationIDs, attendeeNames } };
  }
  const response = await notificationsClient.startExport(params, await getAuthToken());

  if (!response.ok) {
    const error = (await response.json().catch(() => ({ error: 'Failed to start export' }))) as {
      error?: string;
    };
    throw new Error(error.error || `Export start failed: ${response.status}`);
  }

  return response.json();
}

export async function checkExportStatus(jobId: string): Promise<ExportStatusResponse> {
  const response = await notificationsClient.exportStatus(jobId, await getAuthToken());

  if (!response.ok) {
    const error: { error?: string } = await response
      .json()
      .catch(() => ({ error: 'Failed to check export status' }));
    throw new Error(error.error || `Status check failed: ${response.status}`);
  }

  return response.json() as Promise<ExportStatusResponse>;
}

export interface SelectionSummary {
  totalCount: number;
  withEmail?: number;
  taxDeductible?: number;
  nonTaxDeductible?: number;
  offlinePayments?: number;
  activeCount?: number;
  scheduledCount?: number;
  canceledCount?: number;
  deletableElementCount?: number;
}

export async function fetchSelectionSummary(params: {
  entityType: string;
  filters: Record<string, any>;
  excludedIds?: string[];
}): Promise<SelectionSummary> {
  const response = await searchReportingFetch(`${OPENSEARCH_URL}/selection-summary`, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: 'Failed to fetch selection summary' }));
    throw new Error(error.error || `Selection summary failed: ${response.status}`);
  }

  return response.json();
}

export interface DonationYearInfo {
  year: number;
  contactCount: number;
  /** Paid currencies across the selection that year, USD first then alphabetical; omitted if truncated. */
  currencies?: string[];
  /**
   * CON-857: distinct contacts per currency that year, keyed like `currencies`; a code
   * whose contact count is ambiguous is absent, and the map is omitted with `currencies`.
   */
  contactCountsByCurrency?: Record<string, number>;
}

export interface ContactDonationYearsResponse {
  totalCount: number;
  years: DonationYearInfo[];
}

export async function fetchContactDonationYears(params: {
  filters: Record<string, any>;
  excludedIds?: string[];
  selectedIds?: string[];
}): Promise<ContactDonationYearsResponse> {
  const response = await searchReportingFetch(`${OPENSEARCH_URL}/contacts/donation-years`, {
    method: 'POST',
    headers: {
      Authorization: await getAuthToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch donation years' }));
    throw new Error(error.error || `Donation years fetch failed: ${response.status}`);
  }

  return response.json();
}
